import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, Platform } from '@ionic/angular';

import { ViewBasePage } from '@shared/pages';
import { RoutesService, TicketsService } from '@shared/services';
import { MyTimestampPipe } from '@shared/pipes';
import type { TTicket, TRoutesViewModel } from '@shared/models';

// Route Page 2 Goals:
// 1) Load all pending tickets
// 2) Select pending tickets.
// 3) Limit the number of selected tickets and passengers to passenger capacity of the selected shuttle.

@Component({
    'selector': 'app-route2TicketSelectionPage',
    'templateUrl': './route2TicketSelection.page.html',
    'styleUrls': ['./route2TicketSelection.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule, MyTimestampPipe ]
})
export class Route2TicketSelectionPage extends ViewBasePage implements OnInit, OnDestroy {
    public vm: TRoutesViewModel;
    public dropoffTickets: any = [];
    public pickupTickets: any = [];

    constructor(private ticketsService: TicketsService, 
        private routesService: RoutesService, injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_ROUTE_PAGE2);
    }

    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        this.vm = super.getViewModel() as TRoutesViewModel;

        try {
            await this.uxHelperService.showSpinner();
            await this.loadPendingTickets();
            this.vm.selectedShuttle.seatsAvailable = this.vm.selectedShuttle.passengerCapacity;

            const tickets = await this.routesService.getTickets(this.vm.selectedShuttle.uid);
            await this.ticketsService.deleteCompletedTickets(tickets);
            await this.routesService.delete(this.vm.selectedShuttle.uid);
            this.initMoreMenu();
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public initMoreMenu(): void {
        this.pushPopoverMenuItem('Show Tour', 'show-route-2-tour');
    }

    public onNextClick() {
        if (this.vm.step2Complete) {
            this.vm.selectedTickets = this.vm.dropoffManifest.selectedTickets
                .concat(this.vm.pickupManifest.selectedTickets);

            super.setViewModel(this.vm);
            this.router.navigateByUrl('/routes/route3Locations');
        } else {
            this.uxHelperService.showAlert('Error', 'No tickets selected.  Please select at least one ticket.');
        }
    }

    private async loadPendingTickets() {
        const tickets = await this.ticketsService.getPendingTickets();

        if (tickets.length === 0) {
            return;
        }

        // Default all the tickets to unselected.
        tickets.forEach(ticket => {
            ticket.selected = false;
            ticket.numPassengersStr = '  (+' + (ticket.numPassengers - 1).toString() + ')';
        });

        this.dropoffTickets = tickets.filter((ticket: TTicket) => (ticket.fromDealership === true));

        this.pickupTickets = tickets.filter((ticket: TTicket) => (ticket.fromDealership === false));


        this.dropoffTickets = this.ticketsService.sortPendingTickets(this.dropoffTickets);
        this.pickupTickets = this.ticketsService.sortPendingTickets(this.pickupTickets);
    }

    public onTicketSelectedClick(ticket) {
        if (ticket.fromDealership) {
            this.potentiallyAddTicketToManifest(ticket, this.vm.dropoffManifest);
        } else {
            this.potentiallyAddTicketToManifest(ticket, this.vm.pickupManifest);
        }
    }

    // https://stackoverflow.com/questions/43202727/uncheck-checkbox-in-angular-2-ngmodelchange-event
    // https://stackoverflow.com/questions/43339450/angular-2-checkbox-not-kept-in-sync/43339577#43339577
    private potentiallyAddTicketToManifest(ticket: TTicket, manifest) {
        if (ticket.selected) {
            // If there is not enough room in the shuttle for this ticket's passengers, then deselect it.
            if (ticket.numPassengers > manifest.seatsAvailable) {
                setTimeout(() => {
                    ticket.selected = false;
                });
                this.uxHelperService.showToast('Not enough seats available.');
                return;
            }

            // There is enough room in the shuttle for this ticket's passengers.
            this.addTicketToSelectedTickets(ticket, manifest.selectedTickets);
            manifest.seatsAvailable -= ticket.numPassengers;
        } else {
            for (let idx = manifest.selectedTickets.length - 1; idx >= 0; idx--) {
                if (manifest.selectedTickets[idx] === ticket) {
                    manifest.selectedTickets.splice(idx, 1);
                    manifest.seatsAvailable += ticket.numPassengers;
                }
            }
        }

        // We can advance to the next slide as long as a minimum of one ticket has been selected.
        const num = this.vm.dropoffManifest.selectedTickets.length +
            this.vm.pickupManifest.selectedTickets.length;
        this.vm.step2Complete = (num > 0);
    }

    private addTicketToSelectedTickets(ticket: TTicket, selectedTickets) {
        let found = false;
        selectedTickets.forEach((tix) => {
            if (tix.uid === ticket.uid) {
                found = true;
                return;
            }
        });
        if (!found) {
            selectedTickets.push(ticket);
        }
    }
}
